<template>
  <Teleport to="body">
    <!-- Barra de modo selección -->
    <div v-if="isPickMode" class="pick-bar">
      <span class="pick-bar__pill">
        <i class="fas fa-crosshairs" aria-hidden="true"></i>
        Modo selección
      </span>
      <p class="pick-bar__hint">
        Haz clic en un elemento editable de la web para abrirlo en el editor de Diseño.
      </p>
      <button type="button" class="pick-bar__btn" @click="onCancel">
        <i class="fas fa-times" aria-hidden="true"></i>
        Cancelar
      </button>
    </div>

    <!-- Aviso: selección solo en PC -->
    <div v-if="mobileGateOpen" class="pick-gate" @click.self="closeGate">
      <div class="pick-gate__card" role="alertdialog" aria-modal="true" aria-label="Selección no disponible en móviles">
        <div class="pick-gate__icon"><i class="fas fa-desktop" aria-hidden="true"></i></div>
        <h2 class="pick-gate__title">Selección disponible solo en computadora</h2>
        <p class="pick-gate__text">
          La selección de elementos funciona con hover y clic y está pensada para pantallas grandes.
          Abrí esta sección desde una PC o laptop para usarla.
        </p>
        <span class="pick-gate__badge">
          <i class="fas fa-mobile-alt" aria-hidden="true"></i>
          No disponible en dispositivos móviles
        </span>
        <button type="button" class="pick-gate__btn" @click="closeGate">Entendido</button>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { useToast } from 'vue-toastification'
import { isPickMode, mobileGateOpen, exitPickMode } from '@/composables/usePickMode'

const toast = useToast()

function onCancel() {
  exitPickMode()
  toast.success('Saliste del modo selección')
}

function closeGate() {
  mobileGateOpen.value = false
}
</script>

<style scoped>
.pick-bar {
  position: fixed;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 9000;
  display: flex;
  align-items: center;
  gap: 0.85rem;
  max-width: calc(100vw - 24px);
  padding: 0.5rem 0.6rem;
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 999px;
  background: rgba(20, 20, 26, 0.95);
  box-shadow: 0 14px 44px rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(6px);
  font-family: 'General-Sans', 'Segoe UI', sans-serif;
}

.pick-bar__pill {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  flex-shrink: 0;
  padding: 0.3rem 0.75rem;
  border-radius: 999px;
  background: rgba(127, 43, 195, 0.32);
  color: #d8c6f5;
  font-size: 0.74rem;
  font-weight: 700;
  letter-spacing: 0.03em;
}

.pick-bar__hint {
  margin: 0;
  font-size: 0.76rem;
  color: #c9c9c9;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.pick-bar__btn {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  flex-shrink: 0;
  min-height: 32px;
  padding: 0.28rem 0.8rem;
  border: none;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.08);
  color: #ececec;
  font-size: 0.76rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 130ms ease;
}

.pick-bar__btn:hover { background: rgba(255, 255, 255, 0.16); }

/* ── Gate: solo PC ── */
.pick-gate {
  position: fixed;
  inset: 0;
  z-index: 11000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.2rem;
  background: rgba(8, 8, 12, 0.7);
  backdrop-filter: blur(5px);
}

.pick-gate__card {
  width: 420px;
  max-width: 100%;
  padding: 2.1rem 1.9rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  background: #1b1b1f;
  color: #e8e8e8;
  text-align: center;
  box-shadow: 0 24px 70px rgba(0, 0, 0, 0.55);
  font-family: 'General-Sans', 'Segoe UI', sans-serif;
}

.pick-gate__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 3.2rem;
  height: 3.2rem;
  margin-bottom: 0.9rem;
  border-radius: 12px;
  background: rgba(127, 43, 195, 0.2);
  color: #a68ade;
  font-size: 1.35rem;
}

.pick-gate__title {
  margin: 0 0 0.55rem;
  font-size: 1.05rem;
  font-weight: 700;
  color: #f4f4f4;
}

.pick-gate__text {
  margin: 0 0 1rem;
  font-size: 0.84rem;
  line-height: 1.55;
  color: #a0a0a0;
}

.pick-gate__badge {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  margin-bottom: 1.2rem;
  padding: 0.4rem 0.85rem;
  border-radius: 999px;
  background: rgba(255, 99, 71, 0.12);
  color: #ff9d8a;
  font-size: 0.72rem;
  font-weight: 600;
}

.pick-gate__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 36px;
  padding: 0.45rem 1.4rem;
  border: none;
  border-radius: 999px;
  background: linear-gradient(135deg, #8b5cf6, #7f2bc3);
  color: #fff;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 4px 14px rgba(127, 43, 195, 0.35);
}

.pick-gate__btn:hover { transform: translateY(-1px); }

@media (max-width: 640px) {
  .pick-bar__hint { display: none; }
}
</style>
